import { isMobileDevice } from '@va/dashboard/selectors/ui';
import { PageContent } from '@va/dashboard/shared/ui-layout';
import { CheckmarkIcon, ResetIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { useConfirmationDialogContext, useUnsavedChangesGuard } from '@va/shared/util-confirmation';
import { contributorRole } from '@va/standalone/shared/selectors';
import { PrivacyLevelsEnum } from '@va/standalone/shared/types';
import { Button, HorizontalScroll } from '@va/ui/design-system';
import { useWindowDimensions } from '@va/util/hooks';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CancelPrivacyLevelChangesModal from './CancelPrivacyLevelChangesModal';
import PrivacyLevelCard from './PrivacyLevelCard';
import ResetPrivacyLevelModal from './ResetPrivacyLevelModal';
import usePrivacyLevelOptionsContext, { PrivacyLevelContextProvider } from './usePrivacyLevelOptionsContext';

const PrivacyLevels = () => {
  return (
    <PrivacyLevelContextProvider>
      <PrivacyLevelContent />
    </PrivacyLevelContextProvider>
  );
};

export default PrivacyLevels;

const PrivacyLevelContent = () => {
  const translate = useTranslate();
  const [resetPrivacyLevelModalVisible, setResetPrivacyLevelVisible] = useState(false);
  const isMobile = useSelector(isMobileDevice);
  const isContributor = useSelector(contributorRole);

  const { confirm } = useConfirmationDialogContext();

  const { width } = useWindowDimensions();
  const isSidebarVisible = useMemo(() => width > 1024, [width]);

  const contentWidth = useMemo(() => {
    if (!isSidebarVisible) return '100%';
    return 'calc(100% - 72px)';
  }, [isSidebarVisible]);

  const {
    handlePrivacyLevelChange,
    activePrivacyLevel,
    privacyLevelCountries,
    handleValueChange,
    cancelChanges,
    submitChanges,
    updatePrivacyInProgress,
    hasMadeChanges,
  } = usePrivacyLevelOptionsContext();

  useUnsavedChangesGuard(hasMadeChanges, { id: 'privacy-levels' });

  return (
    <>
      {resetPrivacyLevelModalVisible && (
        <ResetPrivacyLevelModal
          isModalOpen={resetPrivacyLevelModalVisible}
          onClose={() => setResetPrivacyLevelVisible(false)}
        />
      )}
      <PageContent>
        <HorizontalScroll>
          <div className='grid grid-cols-1 md:grid-cols-privacy-levels gap-3 overflow-auto mb-20'>
            <PrivacyLevelCard
              handlePrivacyLevelClick={handlePrivacyLevelChange}
              value={PrivacyLevelsEnum.defaultPrivacy}
              selected={activePrivacyLevel === PrivacyLevelsEnum.defaultPrivacy}
              baseKey={'onboarding.privacyLevels.defaultPrivacy'}
              options={privacyLevelCountries[PrivacyLevelsEnum.defaultPrivacy]}
              handleOnOptionClick={handleValueChange}
            />
            <PrivacyLevelCard
              handlePrivacyLevelClick={handlePrivacyLevelChange}
              value={PrivacyLevelsEnum.basicPrivacy}
              selected={activePrivacyLevel === PrivacyLevelsEnum.basicPrivacy}
              baseKey={'onboarding.privacyLevels.basicPrivacy'}
              options={privacyLevelCountries[PrivacyLevelsEnum.basicPrivacy]}
              handleOnOptionClick={handleValueChange}
            />
            <PrivacyLevelCard
              handlePrivacyLevelClick={handlePrivacyLevelChange}
              value={PrivacyLevelsEnum.GDPRMode}
              selected={activePrivacyLevel === PrivacyLevelsEnum.GDPRMode}
              baseKey={'onboarding.privacyLevels.gdprMode'}
              options={privacyLevelCountries[PrivacyLevelsEnum.GDPRMode]}
              handleOnOptionClick={handleValueChange}
            />
            <PrivacyLevelCard
              handlePrivacyLevelClick={handlePrivacyLevelChange}
              value={PrivacyLevelsEnum.completePrivacy}
              selected={activePrivacyLevel === PrivacyLevelsEnum.completePrivacy}
              baseKey={'onboarding.privacyLevels.completePrivacy'}
              options={privacyLevelCountries[PrivacyLevelsEnum.completePrivacy]}
              handleOnOptionClick={handleValueChange}
            />
          </div>
        </HorizontalScroll>
        <div
          style={{ width: contentWidth }}
          className='flex gap-18px py-18px border-t-2 border-solid border-gray-gallery px-30px fixed bottom-0 z-999 bg-white right-0 ease-intransition-all duration-200 ease-in-out'
        >
          {!isMobile && (
            <Button
              text={translate('button.resetAllSettings')}
              color='tertiary'
              icon={(_, color) => <ResetIcon color={color} />}
              onClick={() => setResetPrivacyLevelVisible(true)}
              disabled={isContributor}
              tooltip={isContributor ? translate('tooltips.contributorRole') : undefined}
            />
          )}
          <div className='flex gap-18px w-full'>
            <Button
              text={translate('button.cancelChanges')}
              className='w-full'
              color='tertiary'
              disabled={isContributor || !hasMadeChanges}
              tooltip={isContributor ? translate('tooltips.contributorRole') : undefined}
              onClick={() => confirm(() => cancelChanges(), CancelPrivacyLevelChangesModal)}
            />
            <Button
              text={updatePrivacyInProgress ? translate('button.loading') : translate('button.saveChanges')}
              className='w-full justify-center'
              rightSideIcon={<CheckmarkIcon color={'#FFFFFF'} />}
              rightSideIconWrapperCss='ml-0!'
              onClick={submitChanges}
              disabled={isContributor || updatePrivacyInProgress || !hasMadeChanges}
              tooltip={isContributor ? translate('tooltips.contributorRole') : undefined}
            />
          </div>
        </div>
      </PageContent>
    </>
  );
};
